import type { MarkRange, RawCommands } from '@tiptap/core';
import { getMarksBetween, getNodeType } from '@tiptap/core';
import type { NodeType } from '@tiptap/pm/model';
import type { EditorState } from '@tiptap/pm/state';
import type { EditorView } from '@tiptap/pm/view';
import { Node_Type } from 'ricos-schema';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    updateTextNode: {
      /**
       * Update selected text nodes.
       */
      updateTextNode: (
        typeOrName: string | NodeType,
        attributes?: Record<string, any>
      ) => ReturnType;
    };
  }
}

export function updateBlockType(nodeType: NodeType, attrs: Record<string, any>) {
  return function (state: EditorState, dispatch?: EditorView['dispatch']) {
    const { from, to } = state.selection;
    let applicable = false;
    const tr = state.tr;

    state.doc.nodesBetween(from, to, (node, pos) => {
      if (applicable) return false;
      if (!node.isTextblock || node.hasMarkup(nodeType, attrs)) return;
      if (node.type === nodeType) {
        applicable = true;
      } else {
        const $pos = state.doc.resolve(pos),
          index = $pos.index();
        applicable = $pos.parent.canReplaceWith(index, index + 1, nodeType);
      }
    });
    if (!applicable) return false;
    if (dispatch) {
      state.doc.nodesBetween(from, to, (node, pos) => {
        if (!node.isTextblock || node.hasMarkup(nodeType, attrs)) return;

        const marksBetween = getMarksBetween(from, to, state.doc);
        filterExcessMarks(marksBetween).forEach(m => tr.removeMark(from, to, m.mark));
        tr.setMeta('preventAutolink', true);

        if (nodeType.name === Node_Type.PARAGRAPH) {
          // Switching from a Heading to a Paragraph results in level persistence.
          // Removing the level to prevent any potential side effects.
          tr.setNodeMarkup(pos, nodeType, { ...node.attrs, ...attrs, level: undefined });
        } else {
          tr.setNodeMarkup(pos, nodeType, { ...node.attrs, ...attrs });
        }
      });
      dispatch(tr);
    }
    return true;
  };
}

export const updateTextNode: RawCommands['updateTextNode'] =
  (typeOrName, attributes = {}) =>
  ({ state, dispatch, chain }) => {
    const type = getNodeType(typeOrName, state.schema);

    return (
      chain()
        // try to convert node to default node if needed
        .command(({ commands }) => {
          const canSetBlock = updateBlockType(type, attributes)(state);

          if (canSetBlock) {
            return true;
          }

          return commands.normalizeNodes();
        })
        .command(({ state: updatedState }) => {
          return updateBlockType(type, attributes)(updatedState, dispatch);
        })
        .run()
    );
  };

const filterExcessMarks = (marksBetween: MarkRange[]) => {
  const preserveUnderline = marksBetween.find(m => m.mark.type.name === 'LINK');

  // we want to persist only LINK or LINK & UNDERLINE mark
  return marksBetween.filter(
    m => !(m.mark.type.name === 'LINK' || (m.mark.type.name === 'UNDERLINE' && preserveUnderline))
  );
};
